// client/src/components/analytics/SocialAnalytics.jsx
// Main social media analytics dashboard with interactive charts

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  fetchAnalyticsOverview,
  fetchAnalyticsTrends,
  fetchFollowerGrowth,
  formatMetricNumber,
  PLATFORM_META,
} from '../../services/analyticsApi';
import AppFooter from '../AppFooter';
import { FaArrowLeft, FaHome } from 'react-icons/fa';

const PERIOD_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 90, label: '90 Days' },
];

// All supported platforms for the table (show even if 0 posts)
const ALL_PLATFORMS = Object.keys(PLATFORM_META);

function SocialAnalytics() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant);

  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartType, setChartType] = useState('line');
  const [chartMetric, setChartMetric] = useState('posts');

  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState([]);
  const [followers, setFollowers] = useState(null);

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);

    try {
      const [overviewRes, trendsRes, followersRes] = await Promise.allSettled([
        fetchAnalyticsOverview(tenantId, { days }),
        fetchAnalyticsTrends(tenantId, { days }),
        fetchFollowerGrowth(tenantId, { days: Math.max(days, 30) }),
      ]);

      if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value);
      if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value.trends || []);
      if (followersRes.status === 'fulfilled') setFollowers(followersRes.value);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [tenantId, days]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!tenantId) return <p style={{ color: '#ff6b6b', padding: 16 }}>Invalid tenant</p>;

  const kpis = overview?.kpis || {};
  const platforms = overview?.platforms || [];

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 600, color: '#fff' }}>Social Analytics</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            style={{
              background: '#253866', color: '#fff', border: '1px solid #ffa500',
              borderRadius: 6, padding: '4px 8px', fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            {PERIOD_OPTIONS.map(o => (
              <option key={o.value} value={o.value} style={{ background: '#1a1a2e', color: '#fff' }}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={loadData}
            disabled={loading}
            style={{
              background: '#ffa500', color: '#fff', border: 'none', borderRadius: 6,
              padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? '...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,50,50,0.15)', border: '1px solid #ff4444', borderRadius: 8, padding: '8px 12px', marginBottom: 12, color: '#ff8888', fontSize: '0.85rem' }}>
          {error}
          <span onClick={() => setError(null)} style={{ float: 'right', cursor: 'pointer', fontWeight: 700 }}>&times;</span>
        </div>
      )}

      {loading && !overview ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div className="spinner-border text-cobalt" role="status"></div>
          <p style={{ marginTop: 12, color: '#a8a8a8' }}>Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* KPI Summary Cards */}
          <KPISummary kpis={kpis} />

          {/* Chart section */}
          <div style={{ background: '#2a2a2a', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
            {/* Chart header - single compact line */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '6px 12px', background: '#253866', gap: 8,
            }}>
              <select
                value={chartMetric}
                onChange={e => setChartMetric(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 4, padding: '3px 8px', fontSize: '0.8rem', cursor: 'pointer',
                }}
              >
                <option value="posts" style={{ background: '#1a1a2e', color: '#fff' }}>Posts Published</option>
                <option value="engagement" style={{ background: '#1a1a2e', color: '#fff' }}>Engagement</option>
                <option value="likes" style={{ background: '#1a1a2e', color: '#fff' }}>Likes</option>
                <option value="comments" style={{ background: '#1a1a2e', color: '#fff' }}>Comments</option>
                <option value="shares" style={{ background: '#1a1a2e', color: '#fff' }}>Shares</option>
                <option value="reach" style={{ background: '#1a1a2e', color: '#fff' }}>Reach</option>
                <option value="impressions" style={{ background: '#1a1a2e', color: '#fff' }}>Impressions</option>
              </select>
              <div style={{ display: 'flex', gap: 0 }}>
                <button
                  onClick={() => setChartType('bar')}
                  style={{
                    background: chartType === 'bar' ? '#ffa500' : 'rgba(255,255,255,0.1)',
                    color: '#fff', border: 'none', borderRadius: '4px 0 0 4px',
                    padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Bar
                </button>
                <button
                  onClick={() => setChartType('line')}
                  style={{
                    background: chartType === 'line' ? '#ffa500' : 'rgba(255,255,255,0.1)',
                    color: '#fff', border: 'none', borderRadius: '0 4px 4px 0',
                    padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Line
                </button>
              </div>
            </div>
            {/* Chart body - no padding, 90% width */}
            <div style={{ width: '90%', margin: '0 auto', padding: '8px 0 4px 0' }}>
              <TrendChart trends={trends} chartType={chartType} metric={chartMetric} />
            </div>
          </div>

          {/* Platform Breakdown Chart */}
          {platforms.length > 0 && (
            <div style={{ background: '#2a2a2a', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
              <div style={{ padding: '6px 12px', background: '#253866' }}>
                <h6 style={{ margin: 0, fontSize: '0.85rem', color: '#fff', fontWeight: 600 }}>Posts by Platform</h6>
              </div>
              <div style={{ width: '90%', margin: '0 auto', padding: '8px 0 4px 0' }}>
                <PlatformChart platforms={platforms} />
              </div>
            </div>
          )}

          {/* Platform Table - ALL platforms */}
          <PlatformTable platforms={platforms} />

          {/* Follower Growth — show every connected platform, even with no snapshot yet */}
          {platforms.some(p => p.connected) && <FollowerGrowth data={followers} platforms={platforms} />}

          {/* Navigation buttons */}
          <div style={{ textAlign: 'center', marginBottom: 16, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate(`/${tenant}/analytics/posts`)}
              style={{
                background: '#253866', color: '#fff', border: '2px solid #ffa500',
                borderRadius: 8, padding: '10px 24px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              View Per-Post Analytics
            </button>
            <button
              onClick={() => navigate(`/${tenant}/replies`)}
              style={{
                background: '#253866', color: '#fff', border: '2px solid #ffa500',
                borderRadius: 8, padding: '10px 24px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Manage Replies
            </button>
          </div>

          {/* Empty state */}
          {!kpis.postsPublished && !loading && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#a8a8a8' }}>
              <h5 style={{ color: '#e6e6e6' }}>No Posts Yet</h5>
              <p style={{ fontSize: '0.85rem' }}>Publish posts to your connected social platforms to see analytics.</p>
            </div>
          )}
        </>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

// ============================================================================
// KPI Summary Cards
// ============================================================================
function KPISummary({ kpis }) {
  const cards = [
    { label: 'Posts', value: kpis.postsPublished || 0, color: '#ffa500' },
    { label: 'Likes', value: kpis.totalLikes || 0, color: '#E91E63' },
    { label: 'Comments', value: kpis.totalComments || 0, color: '#2196F3' },
    { label: 'Shares', value: kpis.totalShares || 0, color: '#FF9800' },
    { label: 'Reach', value: kpis.totalReach || 0, color: '#4CAF50' },
    { label: 'Eng. Rate', value: `${kpis.engagementRate || 0}%`, color: '#9C27B0', isText: true },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
      {cards.map((c, i) => (
        <div key={i} style={{
          background: '#2a2a2a', borderRadius: 8, textAlign: 'center', padding: '10px 6px',
          borderTop: `3px solid ${c.color}`,
        }}>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#fff' }}>
            {c.isText ? c.value : formatMetricNumber(c.value)}
          </div>
          <div style={{ fontSize: '0.65rem', color: '#a8a8a8', marginTop: 2 }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Trend Chart (Bar or Line) - large height
// ============================================================================
function TrendChart({ trends, chartType, metric }) {
  if (!trends.length) {
    return (
      <div style={{ textAlign: 'center', color: '#a8a8a8', padding: '40px 0' }}>
        <p style={{ fontSize: '0.85rem', margin: 0 }}>No trend data available for this period.</p>
      </div>
    );
  }

  const data = trends.map(t => ({
    ...t,
    label: new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));

  const metricConfig = {
    posts: { dataKey: 'posts', name: 'Posts', color: '#ffa500' },
    engagement: { dataKey: 'engagement', name: 'Engagement', color: '#4CAF50' },
    likes: { dataKey: 'likes', name: 'Likes', color: '#E91E63' },
    comments: { dataKey: 'comments', name: 'Comments', color: '#2196F3' },
    shares: { dataKey: 'shares', name: 'Shares', color: '#FF9800' },
    reach: { dataKey: 'reach', name: 'Reach', color: '#9C27B0' },
    impressions: { dataKey: 'impressions', name: 'Impressions', color: '#795548' },
  };

  const cfg = metricConfig[metric] || metricConfig.posts;
  const ChartComponent = chartType === 'line' ? LineChart : BarChart;

  return (
    <ResponsiveContainer width="100%" height={320}>
      <ChartComponent data={data} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#a8a8a8' }} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 10, fill: '#a8a8a8' }} />
        <Tooltip
          contentStyle={{ background: '#1a1a1a', border: '1px solid #444', borderRadius: 6, fontSize: 12, color: '#fff' }}
          labelStyle={{ color: '#ffa500' }}
          formatter={(val) => [formatMetricNumber(val), cfg.name]}
        />
        {chartType === 'line' ? (
          <Line
            type="monotone"
            dataKey={cfg.dataKey}
            name={cfg.name}
            stroke={cfg.color}
            strokeWidth={2}
            dot={{ r: 3, fill: cfg.color }}
            activeDot={{ r: 5 }}
          />
        ) : (
          <Bar dataKey={cfg.dataKey} name={cfg.name} fill={cfg.color} radius={[3, 3, 0, 0]} />
        )}
      </ChartComponent>
    </ResponsiveContainer>
  );
}

// ============================================================================
// Platform Horizontal Bar Chart
// ============================================================================
function PlatformChart({ platforms }) {
  const data = platforms.map(p => {
    const meta = PLATFORM_META[p.platform] || { name: p.platform, color: '#666' };
    return { name: meta.name, posts: p.posts, color: meta.color };
  });

  return (
    <ResponsiveContainer width="100%" height={Math.max(120, platforms.length * 40)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis type="number" tick={{ fontSize: 10, fill: '#a8a8a8' }} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#e6e6e6' }} width={80} />
        <Tooltip
          contentStyle={{ background: '#1a1a1a', border: '1px solid #444', borderRadius: 6, fontSize: 12, color: '#fff' }}
        />
        <Bar dataKey="posts" name="Posts" radius={[0, 3, 3, 0]} fill="#ffa500" />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ============================================================================
// Platform Table - shows ALL platforms
// ============================================================================
function PlatformTable({ platforms }) {
  const dataMap = {};
  for (const p of platforms) {
    dataMap[p.platform] = p;
  }

  const allRows = ALL_PLATFORMS.map(key => {
    const meta = PLATFORM_META[key];
    const d = dataMap[key] || {};
    return {
      platform: key,
      name: meta.name,
      color: meta.color,
      icon: meta.icon,
      posts: d.posts || 0,
      likes: d.likes || 0,
      comments: d.comments || 0,
      shares: d.shares || 0,
      reach: d.reach || 0,
      engagementRate: d.engagementRate || 0,
      hasMetrics: d.hasMetrics || false,
    };
  });

  const hasAnyMetrics = allRows.some(r => r.hasMetrics);

  return (
    <div style={{ background: '#2a2a2a', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ padding: '6px 12px', background: '#253866' }}>
        <h6 style={{ margin: 0, fontSize: '0.85rem', color: '#fff', fontWeight: 600 }}>
          {hasAnyMetrics ? 'Performance by Platform' : 'Activity by Platform'}
        </h6>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #444' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: '#a8a8a8', fontWeight: 600 }}>Platform</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', color: '#a8a8a8', fontWeight: 600 }}>Posts</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', color: '#a8a8a8', fontWeight: 600 }}>Likes</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', color: '#a8a8a8', fontWeight: 600 }}>Comments</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', color: '#a8a8a8', fontWeight: 600 }}>Shares</th>
              {hasAnyMetrics && <th style={{ textAlign: 'right', padding: '8px 10px', color: '#a8a8a8', fontWeight: 600 }}>Reach</th>}
            </tr>
          </thead>
          <tbody>
            {allRows.map((r, i) => (
              <tr key={r.platform} style={{ borderBottom: '1px solid #333', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                <td style={{ padding: '7px 10px', color: '#e6e6e6' }}>
                  <i className={`bi ${r.icon} mr-1`} style={{ color: r.color }}></i>
                  {r.name}
                </td>
                <td style={{ textAlign: 'right', padding: '7px 10px', color: r.posts > 0 ? '#fff' : '#555' }}>{r.posts}</td>
                <td style={{ textAlign: 'right', padding: '7px 10px', color: r.likes > 0 ? '#fff' : '#555' }}>{formatMetricNumber(r.likes)}</td>
                <td style={{ textAlign: 'right', padding: '7px 10px', color: r.comments > 0 ? '#fff' : '#555' }}>{formatMetricNumber(r.comments)}</td>
                <td style={{ textAlign: 'right', padding: '7px 10px', color: r.shares > 0 ? '#fff' : '#555' }}>{formatMetricNumber(r.shares)}</td>
                {hasAnyMetrics && <td style={{ textAlign: 'right', padding: '7px 10px', color: r.reach > 0 ? '#fff' : '#555' }}>{formatMetricNumber(r.reach)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Follower Growth
// ============================================================================
function FollowerGrowth({ data, platforms = [] }) {
  const connected = platforms.filter(p => p.connected);
  if (!connected.length) return null;

  const byPlatform = {};
  for (const row of (data?.current || [])) {
    byPlatform[String(row.platform).toLowerCase()] = row;
  }

  return (
    <div style={{ background: '#2a2a2a', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ padding: '6px 12px', background: '#253866' }}>
        <h6 style={{ margin: 0, fontSize: '0.85rem', color: '#fff', fontWeight: 600 }}>Follower Growth</h6>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, padding: 12 }}>
        {connected.map((cp) => {
          const key = String(cp.platform).toLowerCase();
          const meta = PLATFORM_META[key] || { name: cp.platform, color: '#666', icon: 'bi-globe' };
          const row = byPlatform[key];
          const hasData = !!row;
          const isPositive = hasData && row.growth >= 0;
          let growthColor = '#777';
          if (hasData) growthColor = isPositive ? '#4CAF50' : '#f44336';
          let growthText = 'No snapshot yet';
          if (hasData) {
            const sign = isPositive ? '+' : '';
            growthText = `${sign}${formatMetricNumber(row.growth)} (${row.growthRate}%)`;
          }
          return (
            <div key={key} style={{ background: '#333', borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
              <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: '1rem' }}></i>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff' }}>
                {hasData ? formatMetricNumber(row.currentFollowers) : '—'}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#a8a8a8' }}>{meta.name}</div>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: growthColor }}>
                {growthText}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default SocialAnalytics;
